/**
 * Gold v2 phase 3b: the position and header figures as the backend serves
 * them (gold.v_position / gold.portfolio_current, glossary names). The
 * screens format these fields; they never compute them (policy P5).
 * null = unknown, shown as "—" (P3).
 */

/** The gold columns on every open position (camelCase of the API fields). */
export interface GoldPositionFields {
  isOpen?: boolean | null;              // false = a leg closed today (counts in Today only)
  strategy?: 'cc' | 'csp' | null;
  contractTicker?: string | null;
  premiumReceived?: number | null;      // USD
  stockEntryPrice?: number | null;      // share cost (never the roll-day price)
  legStartPrice?: number | null;
  capitalCommitted?: number | null;
  marketValue?: number | null;
  weight?: number | null;               // fraction
  costBasis?: number | null;            // broker / tax cost per share
  netBasis?: number | null;             // campaign net basis per share
  cushion?: number | null;              // fraction
  stockPnl?: number | null;             // current leg
  optionPnl?: number | null;
  premiumIncome?: number | null;
  costOfCappedUpside?: number | null;
  timeDecay?: number | null;
  priceEffect?: number | null;
  ivEffect?: number | null;
  dividends?: number | null;
  totalPnl?: number | null;             // the current leg
  campaignPnl?: number | null;
  todayPnl?: number | null;             // New York day, both legs
  positionReturn?: number | null;
  thetaPerDay?: number | null;          // position-signed USD/day
  netDelta?: number | null;             // shares
  premiumCapturedPct?: number | null;   // fraction of max profit
  buybackCost?: number | null;
  pnlIfAssigned?: number | null;
  maxOptionPnl?: number | null;
  premiumLeft?: number | null;
  remainingYieldAnn?: number | null;    // fraction per year
  earningsInside?: boolean | null;
  optionIsStale?: boolean | null;
  markAsOf?: string | null;
  holdingConfirmationNeeded?: boolean | null;
}

/** API (snake_case) -> GoldPositionFields. Copies only; no formula. */
export function goldFieldsFrom(p: any): GoldPositionFields {
  return {
    isOpen: p.is_open,
    strategy: p.strategy,
    contractTicker: p.contract_ticker,
    premiumReceived: p.premium_received,
    stockEntryPrice: p.stock_entry_price,
    legStartPrice: p.leg_start_price,
    capitalCommitted: p.capital_committed,
    marketValue: p.market_value,
    weight: p.weight,
    costBasis: p.cost_basis,
    netBasis: p.net_basis,
    cushion: p.cushion,
    stockPnl: p.stock_pnl,
    optionPnl: p.option_pnl,
    premiumIncome: p.premium_income,
    costOfCappedUpside: p.cost_of_capped_upside,
    timeDecay: p.time_decay,
    priceEffect: p.price_effect,
    ivEffect: p.iv_effect,
    dividends: p.dividends,
    totalPnl: p.total_pnl,
    campaignPnl: p.campaign_pnl,
    todayPnl: p.today_pnl,
    positionReturn: p.position_return,
    thetaPerDay: p.theta_per_day,
    netDelta: p.net_delta,
    premiumCapturedPct: p.premium_captured_pct,
    buybackCost: p.buyback_cost,
    pnlIfAssigned: p.pnl_if_assigned,
    maxOptionPnl: p.max_option_pnl,
    premiumLeft: p.premium_left,
    remainingYieldAnn: p.remaining_yield_ann,
    earningsInside: p.earnings_inside,
    optionIsStale: p.option_is_stale,
    markAsOf: p.mark_as_of,
    holdingConfirmationNeeded: p.holding_confirmation_needed,
  };
}

/** One gold.portfolio_current row: every header is the sum of its rows. */
export interface PortfolioSummary {
  strategy: 'all' | 'cc' | 'csp';
  value: number | null;               // glossary portfolio_value
  value_missing: number;
  capital_committed: number | null;   // glossary capital_at_risk
  today_pnl: number | null;
  today_pnl_missing: number;
  total_pnl: number | null;           // glossary position_total_pnl
  total_pnl_missing: number;
  theta_per_day: number | null;
  net_delta: number | null;
  avg_yield: number | null;           // remaining yield, capital-weighted (fraction)
  position_count: number;
  rules_fired: number | null;         // Pro only
  mtd_pnl: number | null;
  shares_to_confirm?: number | null;
}

/** Glossary assigned_shares_confirmation: shares assigned from a put, no call on them since. */
export interface AssignedShares {
  campaign_id: string;
  symbol: string;
  shares: number | null;
  assigned_strike: number | null;
  assigned_put_premium: number | null;
  assigned_on: string | null;
  put_position_id: string | null;
  holding_confirmed: boolean | null;
  confirmed_at: string | null;
  needs_confirmation: boolean;
  stock_price: number | null;
  stock_price_kind: string | null;
  stock_price_as_of: string | null;
}

/** POST /api/portfolio/positions/{id}/close-preview at the user's fill. */
export interface ClosePreview {
  position_id: string;
  fill: number;
  buyback_cost: number | null;
  option_pnl_at_fill: number | null;
  total_pnl_at_fill: number | null;
  stock_pnl: number | null;
  dividends: number | null;
  option_pnl_now: number | null;
  option_price: number | null;
  option_is_stale: boolean | null;
  premium_received: number | null;
}
